/**
 * Aliases entre nomes VisionFT e PowerFT do mesmo canal logico.
 *
 * Gerado cruzando visionft_enums.rs (visionft_id -> nome) e
 * powerft-channels.model.ts (powerft_id -> nome) atraves do par
 * (visionft_id, powerft_id) do ftcloud-channel-dictionary.json.
 *
 * Uso: quando o backend converter um .ftml para Parquet, as colunas vem
 * com nomes PowerFT (ex.: RPMIndex). Pra um pedido feito com nome VisionFT
 * funcionar tanto em .ftlog quanto .ftml, o componente envia VisionFT + PowerFT
 * alias e renomeia a coluna de volta para o nome que o usuario selecionou.
 */

/** VisionFT name -> PowerFT enum name (quando existe mapeamento). */
export const VISIONFT_TO_POWERFT: Readonly<Record<string, string>> = {
  'RPM': 'RPMIndex',
  'RPM_Control': 'RPM_ControlIndex',
  'RPM_LoRes': 'DisplayRPMIndex',
  'RPM_HiRes': 'RPM2Index',
  'RPM_Cam': 'RPM_CAN_Index',
  'MAP': 'MAPIndex',
  'PedalPos': 'PedalPosIndex',
  'TPS': 'TPSIndex',
  'Battery_Voltage': 'VBatIndex',
  'Injection_Time_A': 'InjBankAIndex',
  'Injection_Duty_A': 'InjBankADutyIndex',
  'Injection_Time_B': 'InjBankBIndex',
  'Injection_Duty_B': 'InjBankBDutyIndex',
  'Air_Temperature': 'AirTempIndex',
  'Engine_Temperature': 'EngineTempIndex',
  'WheelieCut': 'BurnoutMasterSignalIndex',
  'Ignition_Dwell': 'IgnDwellIndex',
  'BoostRPM_1': 'Boost1RPMIndex',
  'BoostRPM_2': 'Boost2RPMIndex',
  'EGate_MAP_Target': 'EGate_MapTarget_Index',
  'InjO2CL_Target': 'LambdaTargetIndex',
  'InjO2CL_Value': 'LambdaMF_Index',
  'InjO2CL_Correction': 'LambdaCorrectionIndex',
  'ProNitro_CylTrim_1': 'InputShaftRPMIndex',
  'Clutch_Slip': 'Clutch_Slip_Math',
  'EGT_Cil01': 'Cylinder_1_EGTIndex',
  'EGT_Cil02': 'Cylinder_2_EGTIndex',
  'EGT_Cil03': 'Cylinder_3_EGTIndex',
  'EGT_Cil04': 'Cylinder_4_EGTIndex',
  'EGT_Cil05': 'Cylinder_5_EGTIndex',
  'EGT_Cil06': 'Cylinder_6_EGTIndex',
  'EGT_Cil07': 'Cylinder_7_EGTIndex',
  'EGT_Cil08': 'Cylinder_8_EGTIndex',
  'EGT_Cil09': 'Cylinder_9_EGTIndex',
  'EGT_Cil10': 'Cylinder_10_EGTIndex',
  'EGT_Cil11': 'Cylinder_11_EGTIndex',
  'EGT_Cil12': 'Cylinder_12_EGTIndex',
  'EGT_Cil13': 'Cylinder_13_EGTIndex',
  'EGT_Cil14': 'Cylinder_14_EGTIndex',
  'EGT_Cil15': 'Cylinder_15_EGTIndex',
  'EGT_Cil16': 'Cylinder_16_EGTIndex',
  'EGT_Right': 'Right_Bank_EGTIndex',
  'EGT_Left': 'Left_Bank_EGTIndex',
  'FuelPump_Btn': 'FlexFuel_Ign_Main_Index',
  'VCUData_Vago054': 'FlexFuel_Inj_Lambda_Index',
  'VCUData_Vago055': 'FlexFuel_Inj_Main_Index',
  'FlexFuel_Ethanol': 'Fuel_EthanolPerc_Index',
  'Fuel_Temperature': 'Fuel_Temperature_Index',
  'Fuel_Pressure': 'FuelPressureIndex',
  'Fuel_DiffPressure': 'FuelDiffPress_Math',
  'Flow_BankA': 'FuelFlowBankAIndex',
  'Oil_Pressure': 'OilPressureIndex',
  'Idle_Position': 'IdleActuatorPosIndex',
  'Idle_Valve_PWM': 'IdlePWMIndex',
  'AC_Inj_Comp': 'FlexFuel_Inj_Main_Index',
  'Fan1_Inj_Comp': 'Eletro1_InjEnrichIndex',
  'ALS_Ign_Comp': 'ALS_Ign_RetardIndex',
  'ALS_Inj_Comp': 'ALS_Inj_EnrichmentIndex',
  'Gear_Actual': 'ActualGearIndex',
  'HardCut_CutLevel': 'CUT_HARD_CUT_RPM_Index',
  'Engine_Sync_Ok': 'GapSync_Digital_Index',
  'Engine_SyncSensor_Ok': 'SyncSensorOK_Digital_Index',
  'Boost1_BoostP_Btn': 'WstgtCtrl_BoostP_InputIndex',
  'Engine_Crank': 'Engine_Cranking_Digital_Index',
  'Idle_Signal': 'Engine_Idle_Digital_Index',
  'Inj_FirstPulse': 'PrimePulse_Digital_Index',
  'Backpressure_1': 'BackPressure_Index',
  'Inj_FastDecay': 'Inj_Fast_DecayIndex',
  'EGate_Pos1': 'EGate1_Pos_Index',
  'EGate_Pos2': 'EGate2_Pos_Index',
  'EGate_Temp1': 'EGate1_Temp_Index',
  'EGate_Temp2': 'EGate2_Temp_Index',
  'PowerGND': 'VPGNDIndex',
  'VCUData_Vago093': 'Eletro1Index',
  'AC_Button': 'ACInputIndex',
  'Speed_Traction': 'WheelSpeed1Index',
  'Speed_Drag': 'WheelSpeed2Index',
  'Speed_FL': 'WheelSpeed_FLIndex',
  'Speed_FR': 'WheelSpeed_FRIndex',
  'Speed_RL': 'WheelSpeed_RLIndex',
  'Speed_RR': 'WheelSpeed_RRIndex',
  'Acc_Speed': 'SpeedAccelCalcIndex',
  'ATC_Slip': 'TractionSlip_Index',
  'ATC_SlipRate': 'TractionSlipRate_Index',
  'SyncSensor_Pos': 'CamSyncAngleIndex',
  'Acc_Front': 'AccelGIndex',
  'Acc_Lateral': 'LateralGIndex',
  'Pitch_Frontal': 'PitchIndex',
  'PitchRate_Frontal': 'PitchRateAngleIndex',
  'Heading': 'YawAngleIndex',
  'Pitch_Lateral': 'RollIndex',
  'Distance': 'DistanceAccelCalcIndex',
  'PCB_Temp1': 'BoardTemp1Index',
  'PCB_Temp2': 'BoardTemp2Index',
  'ProNitro_CylTrim_2': 'Sensor5VIndex',
  'WheelieRetard': 'Wheelie_RetardIndex',
  'ATC_SlipTarget': 'TractionCtrl_SlipTarget_Index',
  'Inj_Fast': 'FastInj_Digital_Index',
  'Inj_Decay': 'DecayInj_DigitalIndex',
  'Inj_CutOff': 'CutOffInj_DigitalIndex',
  'Boost2_BoostP_Btn': 'WstgtCtrl2_BoostP_InputIndex',
  'BrakePress': 'BrakePressureIndex',
  'SteeringAngle': 'ALS_Switch_Index',
  'Start_Button': 'StarterBtn_SwitchIndex',
  'EGate_P': 'Egate_MAP_PID_P_Val_Index',
  'EGate_I': 'Egate_MAP_PID_I_Val_Index',
  'EGate_D': 'Egate_MAP_PID_D_Val_Index',
  'EGate_PosTarget_1': 'EGate1_PosTarget_Index',
  'EGate_PosTarget_2': 'EGate2_PosTarget_Index',
  'Flow_Total': 'EngineLoad_Index',
  'Datalogger_Btn': 'Datalogger_SwitchIndex',
  'ProgNitro1_Btn': 'Nitro_ButtonIndex',
  'ProgNitro2_Btn': 'Nitro2_ButtonIndex',
  'Shifter5VSensor': 'Shifter5VSensorIndex',
  'PitLimit_Btn': 'PitLimit_SwitchIndex',
  'GenOut_1_Btn': 'GenInput_1_Index',
  'GenOut_2_Btn': 'GenInput_2_Index',
  'GenOut_3_Btn': 'GenInput_3_Index',
  'GenOut_4_Btn': 'GenInput_4_Index',
  'GenOut_5_Btn': 'GenInput_5_Index',
  'GenOut_6_Btn': 'GenInput_6_Index',
  'GenOut_7_Btn': 'GenInput_7_Index',
  'GenOut_8_Btn': 'GenInput_8_Index',
  'Blip_Btn': 'Blip_SwichIndex',
  'ProNitro_Btn': 'ProNitroButtonIndex',
  'Clutch_Btn': 'TwoStep_ClutchBtnIndex',
  '2Step_Btn': 'TwoStep_ClutchBtnIndex',
  'FuelLevel': 'FuelCalc_FuelLevel_Instant_Index',
  'AirComp_Pressure': 'CompAir_Press_Index',
  'BrakePress_F': 'BrakePressureIndex',
  'DayNight_Btn': 'DayNight_SwitchIndex',
  'Transmission_Temperature': 'TransmissionTemperature_Index',
  'P2P_Button': 'P2P_SwitchIndex',
  'YawRate': 'YawRateIndex',
  'RideHeight': 'RideHeightIndex',
  'AnalogShifter': 'Shifter_PosSensorAnlIndex',
  'DigitalShifterA': 'Shifter_PosSensorAIndex',
  'DigitalShifterB': 'Shifter_PosSensorBIndex',
  'DigitalShifterC': 'Shifter_PosSensorCIndex',
  'DigitalShifterD': 'Shifter_PosSensorDIndex',
  'PWMShifter': 'Shifter_PosSensorPWMIndex',
  'Shock_FR': 'ShockSensor_FR_Index',
  'Shock_FL': 'ShockSensor_FL_Index',
  'Shock_BR': 'ShockSensor_RR_Index',
  'Shock_BL': 'ShockSensor_RL_Index',
  'Battery_Temperature': 'Batery_TemperatureIndex',
  'JetSki_NoWake_Active': 'TStop_BumpDownBtn_Index',
  'JetSki_Trim': 'TStop_BumpUpBtn_Index',
  'JetSki_SpdLimit': 'TStop_SuperBumpBtn_Index',
  'ExternalETC': 'ExtTPSRefA_Index',
  'AntiLag_Btn': 'ALS_Switch_Index',
  'Staging_Btn': 'TransbreakPulse_BtnIndex',
  'Start_Button_On': 'StarterBtn_SwitchIndex',
  'Start_Button_Off': 'StarterBtn_Off_Index',
  'MapSelection_1_Btn': 'Adjust_1_Btn_Index',
  'MapSelection_2_Btn': 'Adjust_2_Btn_Index',
  'MapSelection_3_Btn': 'Adjust_3_Btn_Index',
  'MapSelection_4_Btn': 'Adjust_4_Btn_Index',
  'MapSelection_5_Btn': 'Adjust_5_Btn_Index',
  'Dashboard_Btn': 'DashBtn_SwitchIndex',
  'ProNitro_CylTrim_10': 'GearDetect_D_ButtonIndex',
  'Interlock_Button': 'Interlock_Button_Index',
  'NoWake_Button': 'NoWAKE_Btn_Index',
  'ConsumptionReset_Btn': 'FuelCalc_ResetBtn_Index',
  'ProNitro_CylTrim_11': 'GearDetect_R_ButtonIndex',
  'MapChange_Button': 'AdjustChangerBtn_Index',
  'PreStaging_Button': 'Bracket_PreStagingBtn_Index',
  'BrakeSwitch': 'Brake_SwitchIndex',
  'MechFuelInjection_Btn': 'MechFuel_SwitchIndex',
  'ActiveTraction_NextTable_Btn': 'TractionCtrl_Sel_TableUp_Index',
  'ActiveTraction_Table1_Btn': 'TractionCtrl_Sel_Table1_Index',
  'ActiveTraction_Table2_Btn': 'TractionCtrl_Sel_Table2_Index',
  'ActiveTraction_Table3_Btn': 'TractionCtrl_Sel_Table3_Index',
  'ActiveTraction_Table4_Btn': 'TractionCtrl_Sel_Table4_Index',
  'ActiveTraction_Table5_Btn': 'TractionCtrl_Sel_Table5_Index',
  'ActiveTraction_Table6_Btn': 'TractionCtrl_Sel_Table6_Index',
  'ActiveTraction_PrevTable_Btn': 'TractionCtrl_Sel_TableDown_Index',
  'ActiveTraction_Analog': 'TractionCtrl_Sel_AnalogSw_Index',
  'ActiveTraction_ActTbl': 'TractionCtrl_Act_BtnSw_Index',
  'ClutchActive': 'Bracket_BumpDnBtn_Index',
  'PaddleShift_Analog': 'Bracket_BumpUpBtn_Index',
  'JetSki_DirMode': 'Bracket_SuperBumpBtn_Index',
  'Gear_Analog': 'ActualGearAnalogIndex',
  'eGate_BoostP_Btn': 'EGate_BoostP_InputIndex',
  'Frequency_Control': 'MAF_input_Hz_Index',
  'HighBeam': 'HighBeam_SignalIndex',
  'LowBeam': 'LowBeam_SignalIndex',
  'FlexFuel': 'FlexFuelSensor_Index',
  'LockupOn_Button': 'Lockup_OnBtn_Index',
  'LockupOff_Button': 'Lockup_OffBtn_Index',
  'TurnSignal_Right': 'RightTurn_SignalIndex',
  'TurnSignal_Left': 'LeftTurn_SignalIndex',
  'Borb1A': 'Borb1AIndex',
  'Borb1B': 'Borb1BIndex',
  'Borb2A': 'Borb2AIndex',
  'Borb2B': 'Borb2BIndex',
  'Pedal1': 'Pedal1Index',
  'Pedal2': 'Pedal2Index',
  'ExtIgnPoint_1': 'ExtIgn_Ign1_Index',
  'ExtIgnPoint_2': 'ExtIgn_Ign2_Index',
  'ExtIgnPoint_3': 'ExtIgn_Ign3_Index',
  'ExtIgnPoint_4': 'ExtIgn_Ign4_Index',
  'ExtIgnPoint_5': 'ExtIgn_Ign5_Index',
  'ExtIgnPoint_6': 'ExtIgn_Ign6_Index',
  'ExtIgnPoint_7': 'ExtIgn_Ign7_Index',
  'ExtIgnPoint_8': 'ExtIgn_Ign8_Index',
  'Clutch_Position': 'ClutchPositionIndex',
  'IntakeCamPhysPos_1': 'VVTI_Intake_1_SensorIndex',
  'IntakeCamPhysPos_2': 'VVTI_Intake_2_SensorIndex',
  'ExhaustCamPhysPos_1': 'VVTI_Exaust_1_SensorIndex',
  'ExhaustCamPhysPos_2': 'VVTI_Exaust_2_SensorIndex',
  'Clutch_Pressure': 'ClutchPressureIndex',
  'NitrousBottle_Pressure_1': 'NitroPressureIndex',
  'NitrousBottle_Pressure_2': 'NitroPressure_2_Index',
  'Transmission_Pressure': 'TransmissionPressure_Index',
  'Water_Pressure': 'WaterPressureIndex',
  'Wastegate_Pressure_1': 'WstgtCtrl_Pressure_InputIndex',
  'Wastegate_Pressure_2': 'WstgtCtrl2_Pressure_InputIndex',
  'PanVacuum': 'PanVaccun_Index',
  'TorqueConv_Pressure': 'TorqueConvertPressure_Index',
  'ReverseLever': 'Jsk_RevLever_Index',
  'O2_Cil01': 'Cylinder_1_O2Index',
  'O2_Cil02': 'Cylinder_2_O2Index',
  'O2_Cil03': 'Cylinder_3_O2Index',
  'O2_Cil04': 'Cylinder_4_O2Index',
  'O2_Cil05': 'Cylinder_5_O2Index',
  'O2_Cil06': 'Cylinder_6_O2Index',
  'O2_Cil07': 'Cylinder_7_O2Index',
  'O2_Cil08': 'Cylinder_8_O2Index',
  'O2_Cil09': 'Cylinder_9_O2Index',
  'O2_Cil10': 'Cylinder_10_O2Index',
  'O2_Cil11': 'Cylinder_11_O2Index',
  'O2_Cil12': 'Cylinder_12_O2Index',
  'O2_Cil13': 'Cylinder_13_O2Index',
  'O2_Cil14': 'Cylinder_14_O2Index',
  'O2_Cil15': 'Cylinder_15_O2Index',
  'O2_Cil16': 'Cylinder_16_O2Index',
  'O2_Right': 'Right_Bank_O2Index',
  'O2_Left': 'Left_Bank_O2Index',
  'O2_Exaust': 'Exhaust_O2Index',
  'NarrowBand_O2': 'LambdaNarrowIndex',
  'Track_Temperature': 'TrackTemperature_Index',
  'Intercooler_Temp': 'IntercoolerTemperature_Index',
  'Oil_Temperature': 'OilTemperature_Index',
  'Tire_Temperature_FR': 'TireTemperature_FR_Index',
  'Tire_Temperature_FL': 'TireTemperature_FL_Index',
  'Tire_Temperature_RR': 'TireTemperature_RR_Index',
  'Tire_Temperature_RL': 'TireTemperature_RL_Index',
  'FuelFlow_Pump_A': 'Flow_PumpA_Index',
  'FuelFlow_Pump_B': 'Flow_PumpB_Index',
  'FuelFlow_Return_A': 'Flow_ReturnA_Index',
  'FuelFlow_Return_B': 'Flow_ReturnB_Index',
  'FuelFlow_Util_A': 'Flow_Util_A_Index',
  'FuelFlow_Util_B': 'Flow_Util_B_Index',
  'FuelFlow_Util_Total': 'Flow_Util_Total_Index',
  'FuelFlow_Perc_A': 'Flow_Perc_A_Index',
  'FuelFlow_Perc_B': 'Flow_Perc_B_Index',
  'Inj_A_Deadtime': 'DeadTimeBankAIndex',
  'Inj_B_Deadtime': 'DeadTimeBankBIndex',
  'EV_Battery_Charge': 'Batery_VoltageIndex',
  'EV_Inv1_Motor_Meas_RPM': 'EV_Engine_1_RPMIndex',
  'Generic_1': 'Generic1Index',
  'Generic_2': 'Generic2Index',
  'Generic_3': 'Generic3Index',
  'Generic_4': 'Generic4Index',
  'Generic_5': 'Generic5Index',
  'Generic_6': 'Generic6Index',
  'Generic_7': 'Generic7Index',
  'Generic_8': 'Generic8Index',
  'Generic_9': 'Generic9Index',
  'Generic_A': 'Generic10Index',
  'EGate_Duty_1': 'EGate1_DutyCycle_Index',
  'EGate_Duty_2': 'EGate2_DutyCycle_Index',
  'BrakeActive': 'Brake_SwitchIndex',
  'VacuumBrakePressure': 'ServoBrakePressIndex',
  'VacuumBrakeActive': 'HydroVacuumIndex',
  'StrainGauge_P': 'StrainGaugePIndex',
  'StrainGauge_N': 'StrainGaugeNIndex',
  'ETC_1_PWM': 'Borb1APWMIndex',
  'ETC_1_RealPos': 'ETPS1MotorIndex',
  'ETC_2_PWM': 'Borb1BPWMIndex',
  'ETC_2_RealPos': 'ETPS2MotorIndex',
  'VCUData_Vago410': 'GearDetect_D_ButtonIndex',
  'VCUData_Vago411': 'GearDetect_R_ButtonIndex',
  'Bracket_BumpUpBtn': 'Bracket_BumpUpBtn_Index',
  'Bracket_BumpDnBtn': 'Bracket_BumpDnBtn_Index',
  'Bracket_PreStagingBtn': 'Bracket_PreStagingBtn_Index',
  'Bracket_SuperBumpBtn': 'Bracket_SuperBumpBtn_Index',
  'TStop_BumpUpBtn': 'TStop_BumpUpBtn_Index',
  'TStop_BumpDownBtn': 'TStop_BumpDownBtn_Index',
  'TStop_SuperBumpBtn': 'TStop_SuperBumpBtn_Index',
  'FuelCalc_ResetBtn': 'FuelCalc_ResetBtn_Index',
  'FuelCalc_FuelLevel': 'FuelCalc_FuelLevel_Index',
  'FuelCalc_FuelLevel_Instant': 'FuelCalc_FuelLevel_Instant_Index',
  'FuelCalc_Consu_Total': 'FuelCalc_Consu_Total_Index',
  'FuelCalc_Consu_Avg': 'FuelCalc_Consu_Avg_Index',
  'FuelCalc_Consu_Instant': 'FuelCalc_Consu_Instant_Index',
  'FuelCalc_Dist_Total': 'FuelCalc_Dist_Total_Index',
  'FuelCalc_Autonomy': 'FuelCalc_Consu_Instant_Index',
  'Boiler_Temperature': 'WaterTemperature_Index',
  'EGate_1_Flow': 'EGate1_Flow_Index',
  'EGate_2_Flow': 'EGate2_Flow_Index',
  'GearShifter': 'Shifter_SelectionIndex',
  'Custom_1': 'CustomData1Index',
  'Custom_2': 'CustomData2Index',
  'Custom_3': 'CustomData3Index',
  'Custom_4': 'CustomData4Index',
  'Custom_5': 'CustomData5Index',
  'Custom_6': 'CustomData6Index',
  'Custom_7': 'CustomData7Index',
  'Custom_8': 'CustomData8Index',
  'Custom_9': 'CustomData9Index',
  'Custom_10': 'CustomData10Index',
  'Custom_11': 'CustomData11Index',
  'Custom_12': 'CustomData12Index',
  'Custom_14': 'CustomData14Index',
  'Custom_15': 'CustomData15Index',
  'Custom_16': 'CustomData16Index',
  'Custom_17': 'CustomData17Index',
  'Custom_18': 'CustomData18Index',
  'Custom_19': 'CustomData19Index',
  'Custom_20': 'CustomData20Index',
  'Flywheel_RPM': 'FlywheelRPMIndex',
  'Shifter_Park': 'GearDetect_P_ButtonIndex',
  'Shifter_Reverse': 'GearDetect_R_ButtonIndex',
  'Shifter_Neutral': 'GearDetect_N_ButtonIndex',
  'Shifter_Drive': 'GearDetect_D_ButtonIndex',
  'Transbrake_Btn': 'TransbreakPulse_BtnIndex',
  'Transbrake_Pulse': 'TransbreakPulse_PWMIndex',
  'EGate_Volts1': 'CPUTempIndex',
  'GearVolts': 'ALS_Switch_Index',
  'Burnout_Button': 'PitLimit_SwitchIndex',
  'WMI_Protection_Ok': 'ProNitro_StateIndex',
  'ProgNitrous2_On': 'WstgtCtrl2_BoostP_InputIndex',
  'GearChangeOut_OutEn': 'Eletro1Index',
  'P2P_On': 'Eletro2Index',
  'TractionCtrl_On': 'GasPumpIndex',
  'FuelPump_On': 'LineLock_OutputPWMIndex',
  'OutByMAP2_On': 'EGate_BoostP_InputIndex',
  'BlowOff_Act': 'HbridgeFB1Index',
  'C1C2_Current': 'HbridgeFB2Index',
  'C3C4_Current': 'HbridgeFB3Index',
  'C5C6_Current': 'HbridgeFB4Index',
  'TwoStep_Act': 'ThreeStepIndex',
  'ThreeStep_Act': 'Burnout_SwitchIndex',
  'Launch_Ok': 'InjBankACil1Index',
  'Injection_BankA_Cyl1': 'InjBankACil2Index',
  'Injection_BankA_Cyl2': 'InjBankACil3Index',
  'Injection_BankA_Cyl3': 'InjBankACil4Index',
  'Injection_BankA_Cyl4': 'InjBankACil5Index',
  'Injection_BankA_Cyl5': 'InjBankACil6Index',
  'Injection_BankA_Cyl6': 'InjBankACil7Index',
  'Injection_BankA_Cyl7': 'InjBankACil8Index',
  'Injection_BankA_Cyl8': 'InjBankACil9Index',
  'Injection_BankA_Cyl9': 'InjBankACil10Index',
  'Injection_BankA_Cyl10': 'InjBankACil11Index',
  'Injection_BankA_Cyl11': 'InjBankACil12Index',
  'Injection_BankA_Cyl16': 'IgnCil1AdvanceIndex',
  'Ignition_Cyl1': 'IgnCil2AdvanceIndex',
  'Ignition_Cyl2': 'IgnCil3AdvanceIndex',
  'Ignition_Cyl3': 'IgnCil4AdvanceIndex',
  'Ignition_Cyl4': 'IgnCil5AdvanceIndex',
  'Ignition_Cyl5': 'IgnCil6AdvanceIndex',
  'Ignition_Cyl6': 'IgnCil7AdvanceIndex',
  'Ignition_Cyl7': 'IgnCil8AdvanceIndex',
  'Ignition_Cyl8': 'IgnCil9AdvanceIndex',
  'Ignition_Cyl9': 'IgnCil10AdvanceIndex',
  'Ignition_Cyl10': 'IgnCil11AdvanceIndex',
  'Ignition_Cyl11': 'IgnCil12AdvanceIndex',
  'Ignition_Cyl16': 'IgnCutIndex',
  'GearChangeOut_G16': 'MechFuel_Stage1_Digital_Index',
  'MFI_Stage_1': 'MechFuel_Stage2_Digital_Index',
  'MFI_Stage_2': 'MechFuel_Stage3_Digital_Index',
  'MFI_Stage_3': 'MechFuel_Stage4_Digital_Index',
  'MFI_Stage_4': 'MechFuel_Stage5_Digital_Index',
  'MFI_Stage_5': 'MechFuel_Stage6_Digital_Index',
  'MFI_Stage_6': 'MechFuel_Stage7_Digital_Index',
  'MFI_Stage_7': 'MechFuel_Stage8_Digital_Index',
};

/** PowerFT enum name -> VisionFT name (quando existe mapeamento). */
export const POWERFT_TO_VISIONFT: Readonly<Record<string, string>> = {
  'RPMIndex': 'RPM',
  'RPM_ControlIndex': 'RPM_Control',
  'DisplayRPMIndex': 'RPM_LoRes',
  'RPM2Index': 'RPM_HiRes',
  'RPM_CAN_Index': 'RPM_Cam',
  'MAPIndex': 'MAP',
  'PedalPosIndex': 'PedalPos',
  'TPSIndex': 'TPS',
  'VBatIndex': 'Battery_Voltage',
  'InjBankAIndex': 'Injection_Time_A',
  'InjBankADutyIndex': 'Injection_Duty_A',
  'InjBankBIndex': 'Injection_Time_B',
  'InjBankBDutyIndex': 'Injection_Duty_B',
  'AirTempIndex': 'Air_Temperature',
  'EngineTempIndex': 'Engine_Temperature',
  'BurnoutMasterSignalIndex': 'WheelieCut',
  'IgnDwellIndex': 'Ignition_Dwell',
  'Boost1RPMIndex': 'BoostRPM_1',
  'Boost2RPMIndex': 'BoostRPM_2',
  'EGate_MapTarget_Index': 'EGate_MAP_Target',
  'LambdaTargetIndex': 'InjO2CL_Target',
  'LambdaMF_Index': 'InjO2CL_Value',
  'LambdaCorrectionIndex': 'InjO2CL_Correction',
  'InputShaftRPMIndex': 'ProNitro_CylTrim_1',
  'Clutch_Slip_Math': 'Clutch_Slip',
  'Cylinder_1_EGTIndex': 'EGT_Cil01',
  'Cylinder_2_EGTIndex': 'EGT_Cil02',
  'Cylinder_3_EGTIndex': 'EGT_Cil03',
  'Cylinder_4_EGTIndex': 'EGT_Cil04',
  'Cylinder_5_EGTIndex': 'EGT_Cil05',
  'Cylinder_6_EGTIndex': 'EGT_Cil06',
  'Cylinder_7_EGTIndex': 'EGT_Cil07',
  'Cylinder_8_EGTIndex': 'EGT_Cil08',
  'Cylinder_9_EGTIndex': 'EGT_Cil09',
  'Cylinder_10_EGTIndex': 'EGT_Cil10',
  'Cylinder_11_EGTIndex': 'EGT_Cil11',
  'Cylinder_12_EGTIndex': 'EGT_Cil12',
  'Cylinder_13_EGTIndex': 'EGT_Cil13',
  'Cylinder_14_EGTIndex': 'EGT_Cil14',
  'Cylinder_15_EGTIndex': 'EGT_Cil15',
  'Cylinder_16_EGTIndex': 'EGT_Cil16',
  'Right_Bank_EGTIndex': 'EGT_Right',
  'Left_Bank_EGTIndex': 'EGT_Left',
  'FlexFuel_Ign_Main_Index': 'FuelPump_Btn',
  'FlexFuel_Inj_Lambda_Index': 'VCUData_Vago054',
  'FlexFuel_Inj_Main_Index': 'VCUData_Vago055',
  'Fuel_EthanolPerc_Index': 'FlexFuel_Ethanol',
  'Fuel_Temperature_Index': 'Fuel_Temperature',
  'FuelPressureIndex': 'Fuel_Pressure',
  'FuelDiffPress_Math': 'Fuel_DiffPressure',
  'FuelFlowBankAIndex': 'Flow_BankA',
  'OilPressureIndex': 'Oil_Pressure',
  'IdleActuatorPosIndex': 'Idle_Position',
  'IdlePWMIndex': 'Idle_Valve_PWM',
  'Eletro1_InjEnrichIndex': 'Fan1_Inj_Comp',
  'ALS_Ign_RetardIndex': 'ALS_Ign_Comp',
  'ALS_Inj_EnrichmentIndex': 'ALS_Inj_Comp',
  'ActualGearIndex': 'Gear_Actual',
  'CUT_HARD_CUT_RPM_Index': 'HardCut_CutLevel',
  'GapSync_Digital_Index': 'Engine_Sync_Ok',
  'SyncSensorOK_Digital_Index': 'Engine_SyncSensor_Ok',
  'WstgtCtrl_BoostP_InputIndex': 'Boost1_BoostP_Btn',
  'Engine_Cranking_Digital_Index': 'Engine_Crank',
  'Engine_Idle_Digital_Index': 'Idle_Signal',
  'PrimePulse_Digital_Index': 'Inj_FirstPulse',
  'BackPressure_Index': 'Backpressure_1',
  'Inj_Fast_DecayIndex': 'Inj_FastDecay',
  'EGate1_Pos_Index': 'EGate_Pos1',
  'EGate2_Pos_Index': 'EGate_Pos2',
  'EGate1_Temp_Index': 'EGate_Temp1',
  'EGate2_Temp_Index': 'EGate_Temp2',
  'VPGNDIndex': 'PowerGND',
  'Eletro1Index': 'VCUData_Vago093',
  'ACInputIndex': 'AC_Button',
  'WheelSpeed1Index': 'Speed_Traction',
  'WheelSpeed2Index': 'Speed_Drag',
  'WheelSpeed_FLIndex': 'Speed_FL',
  'WheelSpeed_FRIndex': 'Speed_FR',
  'WheelSpeed_RLIndex': 'Speed_RL',
  'WheelSpeed_RRIndex': 'Speed_RR',
  'SpeedAccelCalcIndex': 'Acc_Speed',
  'TractionSlip_Index': 'ATC_Slip',
  'TractionSlipRate_Index': 'ATC_SlipRate',
  'CamSyncAngleIndex': 'SyncSensor_Pos',
  'AccelGIndex': 'Acc_Front',
  'LateralGIndex': 'Acc_Lateral',
  'PitchIndex': 'Pitch_Frontal',
  'PitchRateAngleIndex': 'PitchRate_Frontal',
  'YawAngleIndex': 'Heading',
  'RollIndex': 'Pitch_Lateral',
  'DistanceAccelCalcIndex': 'Distance',
  'BoardTemp1Index': 'PCB_Temp1',
  'BoardTemp2Index': 'PCB_Temp2',
  'Sensor5VIndex': 'ProNitro_CylTrim_2',
  'Wheelie_RetardIndex': 'WheelieRetard',
  'TractionCtrl_SlipTarget_Index': 'ATC_SlipTarget',
  'FastInj_Digital_Index': 'Inj_Fast',
  'DecayInj_DigitalIndex': 'Inj_Decay',
  'CutOffInj_DigitalIndex': 'Inj_CutOff',
  'WstgtCtrl2_BoostP_InputIndex': 'Boost2_BoostP_Btn',
  'BrakePressureIndex': 'BrakePress',
  'ALS_Switch_Index': 'SteeringAngle',
  'StarterBtn_SwitchIndex': 'Start_Button',
  'Egate_MAP_PID_P_Val_Index': 'EGate_P',
  'Egate_MAP_PID_I_Val_Index': 'EGate_I',
  'Egate_MAP_PID_D_Val_Index': 'EGate_D',
  'EGate1_PosTarget_Index': 'EGate_PosTarget_1',
  'EGate2_PosTarget_Index': 'EGate_PosTarget_2',
  'EngineLoad_Index': 'Flow_Total',
  'Datalogger_SwitchIndex': 'Datalogger_Btn',
  'Nitro_ButtonIndex': 'ProgNitro1_Btn',
  'Nitro2_ButtonIndex': 'ProgNitro2_Btn',
  'Shifter5VSensorIndex': 'Shifter5VSensor',
  'PitLimit_SwitchIndex': 'PitLimit_Btn',
  'GenInput_1_Index': 'GenOut_1_Btn',
  'GenInput_2_Index': 'GenOut_2_Btn',
  'GenInput_3_Index': 'GenOut_3_Btn',
  'GenInput_4_Index': 'GenOut_4_Btn',
  'GenInput_5_Index': 'GenOut_5_Btn',
  'GenInput_6_Index': 'GenOut_6_Btn',
  'GenInput_7_Index': 'GenOut_7_Btn',
  'GenInput_8_Index': 'GenOut_8_Btn',
  'Blip_SwichIndex': 'Blip_Btn',
  'ProNitroButtonIndex': 'ProNitro_Btn',
  'TwoStep_ClutchBtnIndex': 'Clutch_Btn',
  'FuelCalc_FuelLevel_Instant_Index': 'FuelLevel',
  'CompAir_Press_Index': 'AirComp_Pressure',
  'DayNight_SwitchIndex': 'DayNight_Btn',
  'TransmissionTemperature_Index': 'Transmission_Temperature',
  'P2P_SwitchIndex': 'P2P_Button',
  'YawRateIndex': 'YawRate',
  'RideHeightIndex': 'RideHeight',
  'Shifter_PosSensorAnlIndex': 'AnalogShifter',
  'Shifter_PosSensorAIndex': 'DigitalShifterA',
  'Shifter_PosSensorBIndex': 'DigitalShifterB',
  'Shifter_PosSensorCIndex': 'DigitalShifterC',
  'Shifter_PosSensorDIndex': 'DigitalShifterD',
  'Shifter_PosSensorPWMIndex': 'PWMShifter',
  'ShockSensor_FR_Index': 'Shock_FR',
  'ShockSensor_FL_Index': 'Shock_FL',
  'ShockSensor_RR_Index': 'Shock_BR',
  'ShockSensor_RL_Index': 'Shock_BL',
  'Batery_TemperatureIndex': 'Battery_Temperature',
  'TStop_BumpDownBtn_Index': 'JetSki_NoWake_Active',
  'TStop_BumpUpBtn_Index': 'JetSki_Trim',
  'TStop_SuperBumpBtn_Index': 'JetSki_SpdLimit',
  'ExtTPSRefA_Index': 'ExternalETC',
  'TransbreakPulse_BtnIndex': 'Staging_Btn',
  'StarterBtn_Off_Index': 'Start_Button_Off',
  'Adjust_1_Btn_Index': 'MapSelection_1_Btn',
  'Adjust_2_Btn_Index': 'MapSelection_2_Btn',
  'Adjust_3_Btn_Index': 'MapSelection_3_Btn',
  'Adjust_4_Btn_Index': 'MapSelection_4_Btn',
  'Adjust_5_Btn_Index': 'MapSelection_5_Btn',
  'DashBtn_SwitchIndex': 'Dashboard_Btn',
  'GearDetect_D_ButtonIndex': 'ProNitro_CylTrim_10',
  'Interlock_Button_Index': 'Interlock_Button',
  'NoWAKE_Btn_Index': 'NoWake_Button',
  'FuelCalc_ResetBtn_Index': 'ConsumptionReset_Btn',
  'GearDetect_R_ButtonIndex': 'ProNitro_CylTrim_11',
  'AdjustChangerBtn_Index': 'MapChange_Button',
  'Bracket_PreStagingBtn_Index': 'PreStaging_Button',
  'Brake_SwitchIndex': 'BrakeSwitch',
  'MechFuel_SwitchIndex': 'MechFuelInjection_Btn',
  'TractionCtrl_Sel_TableUp_Index': 'ActiveTraction_NextTable_Btn',
  'TractionCtrl_Sel_Table1_Index': 'ActiveTraction_Table1_Btn',
  'TractionCtrl_Sel_Table2_Index': 'ActiveTraction_Table2_Btn',
  'TractionCtrl_Sel_Table3_Index': 'ActiveTraction_Table3_Btn',
  'TractionCtrl_Sel_Table4_Index': 'ActiveTraction_Table4_Btn',
  'TractionCtrl_Sel_Table5_Index': 'ActiveTraction_Table5_Btn',
  'TractionCtrl_Sel_Table6_Index': 'ActiveTraction_Table6_Btn',
  'TractionCtrl_Sel_TableDown_Index': 'ActiveTraction_PrevTable_Btn',
  'TractionCtrl_Sel_AnalogSw_Index': 'ActiveTraction_Analog',
  'TractionCtrl_Act_BtnSw_Index': 'ActiveTraction_ActTbl',
  'Bracket_BumpDnBtn_Index': 'ClutchActive',
  'Bracket_BumpUpBtn_Index': 'PaddleShift_Analog',
  'Bracket_SuperBumpBtn_Index': 'JetSki_DirMode',
  'ActualGearAnalogIndex': 'Gear_Analog',
  'EGate_BoostP_InputIndex': 'eGate_BoostP_Btn',
  'MAF_input_Hz_Index': 'Frequency_Control',
  'HighBeam_SignalIndex': 'HighBeam',
  'LowBeam_SignalIndex': 'LowBeam',
  'FlexFuelSensor_Index': 'FlexFuel',
  'Lockup_OnBtn_Index': 'LockupOn_Button',
  'Lockup_OffBtn_Index': 'LockupOff_Button',
  'RightTurn_SignalIndex': 'TurnSignal_Right',
  'LeftTurn_SignalIndex': 'TurnSignal_Left',
  'Borb1AIndex': 'Borb1A',
  'Borb1BIndex': 'Borb1B',
  'Borb2AIndex': 'Borb2A',
  'Borb2BIndex': 'Borb2B',
  'Pedal1Index': 'Pedal1',
  'Pedal2Index': 'Pedal2',
  'ExtIgn_Ign1_Index': 'ExtIgnPoint_1',
  'ExtIgn_Ign2_Index': 'ExtIgnPoint_2',
  'ExtIgn_Ign3_Index': 'ExtIgnPoint_3',
  'ExtIgn_Ign4_Index': 'ExtIgnPoint_4',
  'ExtIgn_Ign5_Index': 'ExtIgnPoint_5',
  'ExtIgn_Ign6_Index': 'ExtIgnPoint_6',
  'ExtIgn_Ign7_Index': 'ExtIgnPoint_7',
  'ExtIgn_Ign8_Index': 'ExtIgnPoint_8',
  'ClutchPositionIndex': 'Clutch_Position',
  'VVTI_Intake_1_SensorIndex': 'IntakeCamPhysPos_1',
  'VVTI_Intake_2_SensorIndex': 'IntakeCamPhysPos_2',
  'VVTI_Exaust_1_SensorIndex': 'ExhaustCamPhysPos_1',
  'VVTI_Exaust_2_SensorIndex': 'ExhaustCamPhysPos_2',
  'ClutchPressureIndex': 'Clutch_Pressure',
  'NitroPressureIndex': 'NitrousBottle_Pressure_1',
  'NitroPressure_2_Index': 'NitrousBottle_Pressure_2',
  'TransmissionPressure_Index': 'Transmission_Pressure',
  'WaterPressureIndex': 'Water_Pressure',
  'WstgtCtrl_Pressure_InputIndex': 'Wastegate_Pressure_1',
  'WstgtCtrl2_Pressure_InputIndex': 'Wastegate_Pressure_2',
  'PanVaccun_Index': 'PanVacuum',
  'TorqueConvertPressure_Index': 'TorqueConv_Pressure',
  'Jsk_RevLever_Index': 'ReverseLever',
  'Cylinder_1_O2Index': 'O2_Cil01',
  'Cylinder_2_O2Index': 'O2_Cil02',
  'Cylinder_3_O2Index': 'O2_Cil03',
  'Cylinder_4_O2Index': 'O2_Cil04',
  'Cylinder_5_O2Index': 'O2_Cil05',
  'Cylinder_6_O2Index': 'O2_Cil06',
  'Cylinder_7_O2Index': 'O2_Cil07',
  'Cylinder_8_O2Index': 'O2_Cil08',
  'Cylinder_9_O2Index': 'O2_Cil09',
  'Cylinder_10_O2Index': 'O2_Cil10',
  'Cylinder_11_O2Index': 'O2_Cil11',
  'Cylinder_12_O2Index': 'O2_Cil12',
  'Cylinder_13_O2Index': 'O2_Cil13',
  'Cylinder_14_O2Index': 'O2_Cil14',
  'Cylinder_15_O2Index': 'O2_Cil15',
  'Cylinder_16_O2Index': 'O2_Cil16',
  'Right_Bank_O2Index': 'O2_Right',
  'Left_Bank_O2Index': 'O2_Left',
  'Exhaust_O2Index': 'O2_Exaust',
  'LambdaNarrowIndex': 'NarrowBand_O2',
  'TrackTemperature_Index': 'Track_Temperature',
  'IntercoolerTemperature_Index': 'Intercooler_Temp',
  'OilTemperature_Index': 'Oil_Temperature',
  'TireTemperature_FR_Index': 'Tire_Temperature_FR',
  'TireTemperature_FL_Index': 'Tire_Temperature_FL',
  'TireTemperature_RR_Index': 'Tire_Temperature_RR',
  'TireTemperature_RL_Index': 'Tire_Temperature_RL',
  'Flow_PumpA_Index': 'FuelFlow_Pump_A',
  'Flow_PumpB_Index': 'FuelFlow_Pump_B',
  'Flow_ReturnA_Index': 'FuelFlow_Return_A',
  'Flow_ReturnB_Index': 'FuelFlow_Return_B',
  'Flow_Util_A_Index': 'FuelFlow_Util_A',
  'Flow_Util_B_Index': 'FuelFlow_Util_B',
  'Flow_Util_Total_Index': 'FuelFlow_Util_Total',
  'Flow_Perc_A_Index': 'FuelFlow_Perc_A',
  'Flow_Perc_B_Index': 'FuelFlow_Perc_B',
  'DeadTimeBankAIndex': 'Inj_A_Deadtime',
  'DeadTimeBankBIndex': 'Inj_B_Deadtime',
  'Batery_VoltageIndex': 'EV_Battery_Charge',
  'EV_Engine_1_RPMIndex': 'EV_Inv1_Motor_Meas_RPM',
  'Generic1Index': 'Generic_1',
  'Generic2Index': 'Generic_2',
  'Generic3Index': 'Generic_3',
  'Generic4Index': 'Generic_4',
  'Generic5Index': 'Generic_5',
  'Generic6Index': 'Generic_6',
  'Generic7Index': 'Generic_7',
  'Generic8Index': 'Generic_8',
  'Generic9Index': 'Generic_9',
  'Generic10Index': 'Generic_A',
  'EGate1_DutyCycle_Index': 'EGate_Duty_1',
  'EGate2_DutyCycle_Index': 'EGate_Duty_2',
  'ServoBrakePressIndex': 'VacuumBrakePressure',
  'HydroVacuumIndex': 'VacuumBrakeActive',
  'StrainGaugePIndex': 'StrainGauge_P',
  'StrainGaugeNIndex': 'StrainGauge_N',
  'Borb1APWMIndex': 'ETC_1_PWM',
  'ETPS1MotorIndex': 'ETC_1_RealPos',
  'Borb1BPWMIndex': 'ETC_2_PWM',
  'ETPS2MotorIndex': 'ETC_2_RealPos',
  'FuelCalc_FuelLevel_Index': 'FuelCalc_FuelLevel',
  'FuelCalc_Consu_Total_Index': 'FuelCalc_Consu_Total',
  'FuelCalc_Consu_Avg_Index': 'FuelCalc_Consu_Avg',
  'FuelCalc_Consu_Instant_Index': 'FuelCalc_Consu_Instant',
  'FuelCalc_Dist_Total_Index': 'FuelCalc_Dist_Total',
  'WaterTemperature_Index': 'Boiler_Temperature',
  'EGate1_Flow_Index': 'EGate_1_Flow',
  'EGate2_Flow_Index': 'EGate_2_Flow',
  'Shifter_SelectionIndex': 'GearShifter',
  'CustomData1Index': 'Custom_1',
  'CustomData2Index': 'Custom_2',
  'CustomData3Index': 'Custom_3',
  'CustomData4Index': 'Custom_4',
  'CustomData5Index': 'Custom_5',
  'CustomData6Index': 'Custom_6',
  'CustomData7Index': 'Custom_7',
  'CustomData8Index': 'Custom_8',
  'CustomData9Index': 'Custom_9',
  'CustomData10Index': 'Custom_10',
  'CustomData11Index': 'Custom_11',
  'CustomData12Index': 'Custom_12',
  'CustomData13Index': 'Custom_12',
  'CustomData14Index': 'Custom_14',
  'CustomData15Index': 'Custom_15',
  'CustomData16Index': 'Custom_16',
  'CustomData17Index': 'Custom_17',
  'CustomData18Index': 'Custom_18',
  'CustomData19Index': 'Custom_19',
  'CustomData20Index': 'Custom_20',
  'FlywheelRPMIndex': 'Flywheel_RPM',
  'GearDetect_P_ButtonIndex': 'Shifter_Park',
  'GearDetect_N_ButtonIndex': 'Shifter_Neutral',
  'TransbreakPulse_PWMIndex': 'Transbrake_Pulse',
  'CPUTempIndex': 'EGate_Volts1',
  'ProNitro_StateIndex': 'WMI_Protection_Ok',
  'Eletro2Index': 'P2P_On',
  'GasPumpIndex': 'TractionCtrl_On',
  'LineLock_OutputPWMIndex': 'FuelPump_On',
  'HbridgeFB1Index': 'BlowOff_Act',
  'HbridgeFB2Index': 'C1C2_Current',
  'HbridgeFB3Index': 'C3C4_Current',
  'HbridgeFB4Index': 'C5C6_Current',
  'ThreeStepIndex': 'TwoStep_Act',
  'Burnout_SwitchIndex': 'ThreeStep_Act',
  'InjBankACil1Index': 'Launch_Ok',
  'InjBankACil2Index': 'Injection_BankA_Cyl1',
  'InjBankACil3Index': 'Injection_BankA_Cyl2',
  'InjBankACil4Index': 'Injection_BankA_Cyl3',
  'InjBankACil5Index': 'Injection_BankA_Cyl4',
  'InjBankACil6Index': 'Injection_BankA_Cyl5',
  'InjBankACil7Index': 'Injection_BankA_Cyl6',
  'InjBankACil8Index': 'Injection_BankA_Cyl7',
  'InjBankACil9Index': 'Injection_BankA_Cyl8',
  'InjBankACil10Index': 'Injection_BankA_Cyl9',
  'InjBankACil11Index': 'Injection_BankA_Cyl10',
  'InjBankACil12Index': 'Injection_BankA_Cyl11',
  'IgnCil1AdvanceIndex': 'Injection_BankA_Cyl16',
  'IgnCil2AdvanceIndex': 'Ignition_Cyl1',
  'IgnCil3AdvanceIndex': 'Ignition_Cyl2',
  'IgnCil4AdvanceIndex': 'Ignition_Cyl3',
  'IgnCil5AdvanceIndex': 'Ignition_Cyl4',
  'IgnCil6AdvanceIndex': 'Ignition_Cyl5',
  'IgnCil7AdvanceIndex': 'Ignition_Cyl6',
  'IgnCil8AdvanceIndex': 'Ignition_Cyl7',
  'IgnCil9AdvanceIndex': 'Ignition_Cyl8',
  'IgnCil10AdvanceIndex': 'Ignition_Cyl9',
  'IgnCil11AdvanceIndex': 'Ignition_Cyl10',
  'IgnCil12AdvanceIndex': 'Ignition_Cyl11',
  'IgnCutIndex': 'Ignition_Cyl16',
  'MechFuel_Stage1_Digital_Index': 'GearChangeOut_G16',
  'MechFuel_Stage2_Digital_Index': 'MFI_Stage_1',
  'MechFuel_Stage3_Digital_Index': 'MFI_Stage_2',
  'MechFuel_Stage4_Digital_Index': 'MFI_Stage_3',
  'MechFuel_Stage5_Digital_Index': 'MFI_Stage_4',
  'MechFuel_Stage6_Digital_Index': 'MFI_Stage_5',
  'MechFuel_Stage7_Digital_Index': 'MFI_Stage_6',
  'MechFuel_Stage8_Digital_Index': 'MFI_Stage_7',
};
